import { sample } from 'lodash-es';
import type { Reducer } from 'react';

export type ChatTraceLogoState = 'entering' | 'exiting' | 'hidden' | 'idle';

export type ChatTraceState = {
  currentIconKey: string;
  logoState: ChatTraceLogoState;
  message: string;
  showLongWaitMessage: boolean;
};

export type ChatTraceEvent =
  | { iconKeys: readonly string[]; type: 'cycle_icon' }
  | { message?: string; type: 'trace_updated' }
  | { type: 'exit_complete' }
  | { type: 'logo_enter_complete' }
  | { type: 'logo_exit_complete' }
  | { type: 'long_wait_triggered' }
  | { type: 'reset' }
  | { type: 'start_exit' };

export const CHAT_TRACE_CONFIG = {
  genericMessages: [
    'Thinking…',
    'Piecing things together…',
    "Making sure I've got the details right…",
    'Waddling through the data…',
  ],
  iconKeys: ['harmony', 'balance', 'flow', 'perception'] as const,
  timing: {
    logoEnterMs: 800,
    logoExitMs: 600,
    longWaitDelayMs: 20000,
  },
} as const;

export type ChatTraceIconKey = (typeof CHAT_TRACE_CONFIG.iconKeys)[number];

const getRandomIconKey = (currentKey?: string): string => {
  const available = CHAT_TRACE_CONFIG.iconKeys.filter((key) => key !== currentKey);
  return sample(available) ?? CHAT_TRACE_CONFIG.iconKeys[0];
};

const getRandomMessage = (): string => {
  return sample(CHAT_TRACE_CONFIG.genericMessages) ?? CHAT_TRACE_CONFIG.genericMessages[0];
};

export const createChatTraceInitialState = (thinkingTraceMessage?: string): ChatTraceState => ({
  currentIconKey: getRandomIconKey(),
  logoState: 'hidden',
  message: thinkingTraceMessage || getRandomMessage(),
  showLongWaitMessage: false,
});

export const chatTraceStateMachine: Reducer<ChatTraceState, ChatTraceEvent> = (state, event) => {
  switch (event.type) {
    case 'trace_updated':
      return {
        ...state,
        message: event.message || getRandomMessage(),
      };

    case 'logo_exit_complete':
      return {
        ...state,
        currentIconKey: getRandomIconKey(state.currentIconKey),
        logoState: 'hidden',
      };

    case 'long_wait_triggered':
      return {
        ...state,
        logoState: 'entering',
        showLongWaitMessage: true,
      };

    case 'logo_enter_complete':
      return {
        ...state,
        logoState: 'idle',
      };

    case 'start_exit':
      if (state.logoState !== 'hidden') {
        return {
          ...state,
          logoState: 'exiting',
        };
      }
      return state;

    case 'exit_complete':
      return {
        ...state,
        logoState: 'hidden',
      };

    case 'cycle_icon': {
      const available = event.iconKeys.filter((key) => key !== state.currentIconKey);
      const newKey = sample(available) ?? event.iconKeys[0] ?? state.currentIconKey;
      return {
        ...state,
        currentIconKey: newKey,
      };
    }

    case 'reset':
      return createChatTraceInitialState();

    default:
      return state;
  }
};
